import { useMemo, useState } from "react";
import classNames from "classnames";
import download from "downloadjs";

import {
  AscFileType,
  CaptionTranslation,
  translationLabel,
} from "@ascendnow/shared";

import AscButton from "../AscButton/AscButton";
import AscModal from "../AscModal/AscModal";
import AscVideoPlayer from "../AscVideoPlayer/AscVideoPlayer";
import styles from "./AscFileViewModal.module.scss";
import AscImage from "../AscImage/AscImage";

export interface AscFileViewModalProps {
  fileType: AscFileType;
  visible: boolean;
  onClose: () => void;
  fileName: string;
  fileUrl: string;
  className?: string;
  videoUrl?: string;
  mimeType?: string;
  onDownload?: (
    downloadPayload: Record<"fileName" | "fileUrl", string> & {
      mimeType?: string;
    }
  ) => void;
  onError?: (error: unknown) => void;
  videoCaptions?: CaptionTranslation[];
}

export default function AscFileViewModal({
  className,
  visible,
  fileName,
  fileUrl,
  fileType,
  videoUrl,
  onClose,
  mimeType,
  onDownload,
  onError,
  videoCaptions,
}: AscFileViewModalProps) {
  const [loading, setLoading] = useState(false);
  const fileView = useMemo(() => {
    switch (fileType) {
      case "image":
        return <AscImage src={fileUrl} className={styles.image} />;
      case "video":
        return (
          <AscVideoPlayer
            className={styles.video}
            url={fileUrl}
            captions={
              videoCaptions && videoCaptions.length > 0
                ? videoCaptions?.map((el) => ({
                    label:
                      translationLabel[
                        el?.language as keyof typeof translationLabel
                      ],
                    srclang: el?.language,
                    src: el?.url,
                  }))
                : []
            }
          />
        );
      case "docs":
        return (
          <object
            data={fileUrl + "#toolbar=0&navpanes=0&scrollbar=0"}
            type="application/pdf"
            width={"100%"}
            height={"100%"}
          >
            <p>Unable to display PDF file.</p>
          </object>
        );
      default:
        return fileName;
    }
  }, [fileType, fileUrl, fileName, visible, videoUrl]);

  function onLoadStart() {
    setLoading(true);
  }

  function onLoadEnd() {
    setLoading(false);
  }

  function handleDownloadClick() {
    if (loading) {
      return;
    }
    if (onDownload) {
      onDownload({ fileName, fileUrl, mimeType });
      return;
    }
    if (!fileUrl) {
      return;
    }

    try {
      if (fileName && mimeType) {
        const request = new XMLHttpRequest();
        request.open("GET", fileUrl, true);
        request.responseType = "blob";
        request.onload = function (e: any) {
          download(e?.target?.response, fileName, mimeType);
        };
        request.onerror = onError || console.error;
        request.onloadstart = onLoadStart;
        request.onloadend = onLoadEnd;
        request.send();
        return;
      }

      download(fileUrl);
    } catch (error: unknown) {
      onError?.(error);
    }
  }

  return (
    <AscModal
      data-testid="asc-file-view-modal"
      modalClassName={classNames(
        styles.ground,
        (fileType === "docs" || fileType === "image") && styles.portrait,
        fileType === "docs" && styles.docs,
        className
      )}
      headerClassName={styles.header}
      header={fileName}
      visible={visible}
      setVisible={onClose}
      footer={
        fileType !== "video" && (
          <AscButton
            loading={loading}
            className={styles.downloadButton}
            onClick={handleDownloadClick}
            tabIndex={1}
          >
            Download
          </AscButton>
        )
      }
      footerClassName={styles.footerContent}
      innerContentClassName={styles.modalContent}
    >
      {fileView}
    </AscModal>
  );
}
